// Package imports:
import React, { useMemo } from 'react';
// Component imports:
import DisplayBox from '../DisplayBox/DisplayBox';
import Tabs from '../../ui-elements/Tabs/Tabs';
import Table from '../../ui-elements/Table/Table';
// Service imports:
import { formatNumber } from '../../services/utils';
import { useFetchedApiLmdData } from '../../services/apiHooks';
// Type imports:
import { IDefaultProps } from '../../types/Types';
import { IApiLmdAvailableReports, IApiLmdQuestorValues } from '../../types/HlutabrefTypes';

const REKSTRAR_KEYS = [{
    key: 910100,
    name: 'Rekstrartekjur'
}, {
    key: 910400,
    name: 'EBITDA'
}, {
    key: 910700,
    name: 'Hagnaður ársins'
}];
var EFNAHAGS_KEYS = [{
    key: 200000,
    name: 'Eignir'
}, {
    key: 300000,
    name: 'Eigið fé'
}, {
    key: 400000,
    name: 'Skuldir'
}, {
    key: 920900,
    name: 'Eigið fé og skuldir'
}];
var SJODSTREYMI_KEYS = [{
    key: 510200,
    name: 'Veltufé frá rekstri'
}, {
    key: 510000,
    name: 'Handbært fé frá rekstri'
}, {
    key: 520000,
    name: 'Fjárfestingarhreyfingar'
}, {
    key: 530000,
    name: 'Fjármögnunarhreyfingar'
}, {
    key: 950700,
    name: 'Breyting á handbæru fé'
}, {
    key: 950900,
    name: 'Handbært fé í lok tímabils'
}];

interface IReportsDatum {
    name: string,
    yearOne: number | string | null,
    yearTwo: number | string | null,
    quarterOne: number | string | null,
    quarterTwo: number | string | null
}

interface IReportsData {
    headers: string[],
    multipliers: IReportsDatum,
    rekstrar: IReportsDatum[],
    efnahags: IReportsDatum[],
    sjodstreymi: IReportsDatum[]
}

interface IOwnProps {
    symbol: string
}
type Props = IOwnProps & IDefaultProps & React.HTMLAttributes<HTMLDivElement>;

const CompanyReports: React.FC<Props> = ({
    symbol,
    accessToken,
    refreshRateMs,
    ...props
}) => {
    const availableReports = useFetchedApiLmdData<IApiLmdAvailableReports[]>(
        `/v1/market_data/v1/questor/available_reports/symbol/${symbol}/periods/[Q1;Q2;Q3;Q4;Y]`,
        accessToken
    );

    const {
        year1,
        year2,
        quarter1,
        quarter2
    } = useMemo(() => {
        const returnObject: {
            year1: IApiLmdAvailableReports | null
            year2: IApiLmdAvailableReports | null
            quarter1: IApiLmdAvailableReports | null
            quarter2: IApiLmdAvailableReports | null
        } = {
            year1: null,
            year2: null,
            quarter1: null,
            quarter2: null
        }
    
        if (availableReports === null || availableReports instanceof Error) return returnObject;
        const availableYears = availableReports.filter(d => d.PeriodName === 'Y');
        const availableQuarters = availableReports.filter(d => d.PeriodName?.startsWith('Q'));

        if (availableYears.length > 0) returnObject.year1 = availableYears[availableYears.length-1] ?? null;
        if (availableYears.length > 1) returnObject.year2 = availableYears[availableYears.length-2] ?? null;
        if (availableQuarters.length > 0) returnObject.quarter1 = availableQuarters[availableQuarters.length-1] ?? null;
        if (availableQuarters.length > 1) returnObject.quarter2 = availableQuarters[availableQuarters.length-2] ?? null;
        return returnObject;
    }, [availableReports]);

    const year1Data = useFetchedApiLmdData<IApiLmdQuestorValues[]>(
        (year1 === null || year1.FiscalYear === null)
            ? null
            : `/v1/market_data/v1/questor/values/period/Y/symbol/${symbol}?year=${year1.FiscalYear}`,
        accessToken
    )
    const year2Data = useFetchedApiLmdData<IApiLmdQuestorValues[]>(
        (year2 === null || year2.FiscalYear === null)
            ? null
            : `/v1/market_data/v1/questor/values/period/Y/symbol/${symbol}?year=${year2.FiscalYear}`,
        accessToken
    )
    const quarter1Data = useFetchedApiLmdData<IApiLmdQuestorValues[]>(
        (quarter1 === null || quarter1.FiscalYear === null || quarter1.PeriodName === null)
            ? null
            : `/v1/market_data/v1/questor/values/period/${quarter1.PeriodName}/symbol/${symbol}?year=${quarter1.FiscalYear}`,
        accessToken
    )
    const quarter2Data = useFetchedApiLmdData<IApiLmdQuestorValues[]>(
        (quarter2 === null || quarter2.FiscalYear === null || quarter2.PeriodName === null)
            ? null
            : `/v1/market_data/v1/questor/values/period/${quarter2.PeriodName}/symbol/${symbol}?year=${quarter2.FiscalYear}`,
        accessToken
    )

    const handleData = () => {
        const data: IReportsData = {
            headers: [],
            multipliers: {
                name: 'Gjaldmiðill',
                yearOne: null,
                yearTwo: null,
                quarterOne: null,
                quarterTwo: null
            },
            rekstrar: [],
            efnahags: [],
            sjodstreymi: []
        };
        for (const { key, name } of REKSTRAR_KEYS) {
            const y1 = (year1Data === null || year1Data instanceof Error) ? null : year1Data.find(d => d.KeyId === key);
            const y2 = (year2Data === null || year2Data instanceof Error) ? null : year2Data.find(d => d.KeyId === key);
            const q1 = (quarter1Data === null || quarter1Data instanceof Error) ? null : quarter1Data.find(d => d.KeyId === key);
            const q2 = (quarter2Data === null || quarter2Data instanceof Error) ? null : quarter2Data.find(d => d.KeyId === key);

            if (data.headers.length === 0) {
                data.headers = [
                    y2?.FiscalYear?.toString() ?? '-',
                    y1?.FiscalYear?.toString() ?? '-',
                    q2?.PeriodName && q2.FiscalYear ? `${q2?.PeriodName} '${q2?.FiscalYear % 100}` : '-',
                    q1?.PeriodName && q1.FiscalYear ? `${q1?.PeriodName} '${q1?.FiscalYear % 100}` : '-'
                ];
            } else {
                if (data.headers[0] === '-') data.headers[0] = y2?.FiscalYear?.toString() ?? '-';
                if (data.headers[1] === '-') data.headers[1] = y1?.FiscalYear?.toString() ?? '-';
                if (data.headers[2] === '-') data.headers[2] = q2?.PeriodName && q2.FiscalYear ? `${q2?.PeriodName} '${q2?.FiscalYear % 100}` : '-';
                if (data.headers[3] === '-') data.headers[3] = q1?.PeriodName && q1.FiscalYear ? `${q1?.PeriodName} '${q1?.FiscalYear % 100}` : '-';
            }
            if (data.multipliers.yearOne === null && y1?.NumbersIn && y1.Currency) {
                data.multipliers.yearOne = `${formatNumber(y1?.NumbersIn, '-', 0)} ${y1?.Currency ?? ''}`;
            }
            if (data.multipliers.yearTwo === null && y2?.NumbersIn && y2.Currency) {
                data.multipliers.yearTwo = `${formatNumber(y2?.NumbersIn, '-', 0)} ${y2?.Currency ?? ''}`;
            }
            if (data.multipliers.quarterOne === null && q1?.NumbersIn && q1.Currency) {
                data.multipliers.quarterOne = `${formatNumber(q1?.NumbersIn, '-', 0)} ${q1?.Currency ?? ''}`;
            }
            if (data.multipliers.quarterTwo === null && q2?.NumbersIn && q2.Currency) {
                data.multipliers.quarterTwo = `${formatNumber(q2?.NumbersIn, '-', 0)} ${q2?.Currency ?? ''}`;
            }
            data.rekstrar.push({
                name: name,
                yearOne: y1?.KeyValue ?? null,
                yearTwo: y2?.KeyValue ?? null,
                quarterOne: q1?.KeyValue ?? null,
                quarterTwo: q2?.KeyValue ?? null
            });
        }
        for (let key of EFNAHAGS_KEYS) {
            const y1 = year1Data === null || year1Data instanceof Error ? null : year1Data.find(d => d.KeyId === key.key);
            const y2 = year2Data === null || year2Data instanceof Error ? null : year2Data.find(d => d.KeyId === key.key);
            const q1 = quarter1Data === null || quarter1Data instanceof Error ? null : quarter1Data.find(d => d.KeyId === key.key);
            const q2 = quarter2Data === null || quarter2Data instanceof Error ? null : quarter2Data.find(d => d.KeyId === key.key);
            if (data.multipliers.yearOne === null && y1?.NumbersIn && y1.Currency) {
                data.multipliers.yearOne = `${formatNumber(y1?.NumbersIn, '-', 0)} ${y1?.Currency ?? ''}`;
            }
            if (data.multipliers.yearTwo === null && y2?.NumbersIn && y2.Currency) {
                data.multipliers.yearTwo = `${formatNumber(y2?.NumbersIn, '-', 0)} ${y2?.Currency ?? ''}`;
            }
            if (data.multipliers.quarterOne === null && q1?.NumbersIn && q1.Currency) {
                data.multipliers.quarterOne = `${formatNumber(q1?.NumbersIn, '-', 0)} ${q1?.Currency ?? ''}`;
            }
            if (data.multipliers.quarterTwo === null && q2?.NumbersIn && q2.Currency) {
                data.multipliers.quarterTwo = `${formatNumber(q2?.NumbersIn, '-', 0)} ${q2?.Currency ?? ''}`;
            }
            data.efnahags.push({
                name: key.name,
                yearOne: y1?.KeyValue ?? null,
                yearTwo: y2?.KeyValue ?? null,
                quarterOne: q1?.KeyValue ?? null,
                quarterTwo: q2?.KeyValue ?? null
            });
        }
        for (let key of SJODSTREYMI_KEYS) {
            const y1 = year1Data === null || year1Data instanceof Error ? null : year1Data.find(d => d.KeyId === key.key);
            const y2 = year2Data === null || year2Data instanceof Error ? null : year2Data.find(d => d.KeyId === key.key);
            const q1 = quarter1Data === null || quarter1Data instanceof Error ? null : quarter1Data.find(d => d.KeyId === key.key);
            const q2 = quarter2Data === null || quarter2Data instanceof Error ? null : quarter2Data.find(d => d.KeyId === key.key);
            if (data.multipliers.yearOne === null && y1?.NumbersIn && y1.Currency) {
                data.multipliers.yearOne = `${formatNumber(y1?.NumbersIn, '-', 0)} ${y1?.Currency ?? ''}`;
            }
            if (data.multipliers.yearTwo === null && y2?.NumbersIn && y2.Currency) {
                data.multipliers.yearTwo = `${formatNumber(y2?.NumbersIn, '-', 0)} ${y2?.Currency ?? ''}`;
            }
            if (data.multipliers.quarterOne === null && q1?.NumbersIn && q1.Currency) {
                data.multipliers.quarterOne = `${formatNumber(q1?.NumbersIn, '-', 0)} ${q1?.Currency ?? ''}`;
            }
            if (data.multipliers.quarterTwo === null && q2?.NumbersIn && q2.Currency) {
                data.multipliers.quarterTwo = `${formatNumber(q2?.NumbersIn, '-', 0)} ${q2?.Currency ?? ''}`;
            }
            data.sjodstreymi.push({
                name: key.name,
                yearOne: y1?.KeyValue ?? null,
                yearTwo: y2?.KeyValue ?? null,
                quarterOne: q1?.KeyValue ?? null,
                quarterTwo: q2?.KeyValue ?? null
            });
        }
        return data;
    }

    const reportsData = handleData();

    return (
        <DisplayBox
            className='KCL_company-reports'
            title="Fjárhagsupplýsingar"
            {...props}
        >
            <Tabs
                tabs={[{
                    id: 'rekstrar',
                    label: 'Rekstrarreikningur',
                    component: <Table
                        data={[reportsData.multipliers, ...reportsData.rekstrar]}
                        columns={[{
                            title: '',
                            dataAccessor: ({name}) => name,
                            textAlign: 'left'
                        }, {
                            title: reportsData.headers[0],
                            dataAccessor: ({yearTwo}) => typeof yearTwo === 'number' ? formatNumber(yearTwo, '-', 0) : yearTwo
                        }, {
                            title: reportsData.headers[1],
                            dataAccessor: ({yearOne}) => typeof yearOne === 'number' ? formatNumber(yearOne, '-', 0) : yearOne
                        }, {
                            title: reportsData.headers[2],
                            dataAccessor: ({quarterTwo}) => typeof quarterTwo === 'number' ? formatNumber(quarterTwo, '-', 0) : quarterTwo
                        }, {
                            title: reportsData.headers[3],
                            dataAccessor: ({quarterOne}) => typeof quarterOne === 'number' ? formatNumber(quarterOne, '-', 0) : quarterOne
                        }]}
                        tableSize='lg'
                    />
                }, {
                    id: 'efnahags',
                    label: 'Efnahagsreikningur',
                    component: <Table
                        data={[reportsData.multipliers, ...reportsData.efnahags]}
                        columns={[{
                            title: '',
                            dataAccessor: ({name}) => name,
                            textAlign: 'left'
                        }, {
                            title: reportsData.headers[0],
                            dataAccessor: ({yearTwo}) => typeof yearTwo === 'number' ? formatNumber(yearTwo, '-', 0) : yearTwo
                        }, {
                            title: reportsData.headers[1],
                            dataAccessor: ({yearOne}) => typeof yearOne === 'number' ? formatNumber(yearOne, '-', 0) : yearOne
                        }, {
                            title: reportsData.headers[2],
                            dataAccessor: ({quarterTwo}) => typeof quarterTwo === 'number' ? formatNumber(quarterTwo, '-', 0) : quarterTwo
                        }, {
                            title: reportsData.headers[3],
                            dataAccessor: ({quarterOne}) => typeof quarterOne === 'number' ? formatNumber(quarterOne, '-', 0) : quarterOne
                        }]}
                        tableSize='lg'
                    />
                }, {
                    id: 'sjodstreymi',
                    label: 'Sjóðstreymi',
                    component: <Table
                        data={[reportsData.multipliers, ...reportsData.sjodstreymi]}
                        columns={[{
                            title: '',
                            dataAccessor: ({name}) => name,
                            textAlign: 'left'
                        }, {
                            title: reportsData.headers[0],
                            dataAccessor: ({yearTwo}) => typeof yearTwo === 'number' ? formatNumber(yearTwo, '-', 0) : yearTwo
                        }, {
                            title: reportsData.headers[1],
                            dataAccessor: ({yearOne}) => typeof yearOne === 'number' ? formatNumber(yearOne, '-', 0) : yearOne
                        }, {
                            title: reportsData.headers[2],
                            dataAccessor: ({quarterTwo}) => typeof quarterTwo === 'number' ? formatNumber(quarterTwo, '-', 0) : quarterTwo
                        }, {
                            title: reportsData.headers[3],
                            dataAccessor: ({quarterOne}) => typeof quarterOne === 'number' ? formatNumber(quarterOne, '-', 0) : quarterOne
                        }]}
                        tableSize='lg'
                    />
                }]}
            />
        </DisplayBox>
    );
}

export default CompanyReports;